import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import { useQueryGetExamSet } from "@/apis/exam_sets/getExamSet";
import { useDeleteExamSet } from "@/apis/exam_sets/deleteExamSet";
import { getPaperTypeLabel } from "@/constants/exam";

const ExamSetDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: examSet, isLoading, error } = useQueryGetExamSet(id || "");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const { mutate: deleteExamSet, isPending: isDeleting } = useDeleteExamSet();

  // Go back to list
  const handleBack = () => {
    navigate("/exam_sets");
  };

  // Go to edit page
  const handleEdit = () => {
    navigate(`/exam_sets/${id}/edit`);
  };

  // Handle delete (archive)
  const handleDelete = () => {
    if (!examSet || isDeleting) return;

    if (window.confirm(`Are you sure you want to delete "${examSet.title}"?`)) {
      setDeleteError(null);

      deleteExamSet(id || "", {
        onSuccess: () => {
          navigate("/exam_sets");
        },
        onError: (error: any) => {
          setDeleteError(error?.message || "Failed to delete exam set");
        },
      });
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className="p-8 text-center">
          <Text>Loading exam set information...</Text>
        </div>
      </PageContainer>
    );
  }

  if (error || !examSet) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Failed to load exam set information. Please try again.</Text>
          <Button onClick={handleBack} className="mt-4">
            Back to List
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        {deleteError && (
          <div className="p-4 bg-red-50 text-red-600 rounded-md">
            {deleteError}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button plain onClick={handleBack}>
              <ArrowLeftIcon className="h-5 w-5" />
              Back
            </Button>

            <div>
              <Heading>Exam Set Details: {examSet.title}</Heading>
              <Text className="text-zinc-500">
                View complete information about this exam set.
              </Text>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleEdit}>
              <PencilIcon className="h-4 w-4" />
              Edit
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <TrashIcon className="h-4 w-4" />
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>

        {/* Exam Set Details */}
        <div className="p-8 border border-gray-200 rounded-md bg-white">
          <div className="space-y-8">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="mb-2 font-medium text-gray-500">Type</div>
                  <div className="mb-4">{examSet.exam_type}</div>
                </div>
              </div>
            </div>

            {/* Included Exam Papers */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Included Exam Papers
              </h3>
              <div className="space-y-6">
                {examSet.items.map((item) => (
                  <div
                    key={item.exam_paper_id}
                    className="p-4 border border-gray-200 rounded-md bg-gray-50"
                  >
                    {/* Title at top, Type below */}
                    <div className="mb-4">
                      <div className="text-base font-semibold text-gray-900 truncate">
                        {item.exam_paper?.title || "-"}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {getPaperTypeLabel(item.exam_paper?.subject || "") ||
                          "-"}
                      </div>
                    </div>
                    {/* Sections stacked vertically */}
                    <div className="flex flex-col gap-4">
                      {item.exam_paper?.sections?.map(
                        (section, sectionIndex) => (
                          <div
                            key={sectionIndex}
                            className="flex-1 p-4 border border-gray-200 rounded-md bg-white"
                          >
                            <div className="font-medium text-gray-700 mb-2">
                              Section {sectionIndex + 1}
                            </div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-gray-500">
                                Duration
                              </span>
                              <span className="text-sm text-gray-900">
                                {section.duration} min
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-500">
                                Questions
                              </span>
                              <span className="text-sm text-gray-900">
                                {section.question_ids
                                  ? section.question_ids.length
                                  : 0}
                              </span>
                            </div>
                          </div>
                        )
                      ) || (
                        <div className="p-4 text-center text-gray-500">
                          No sections available
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default ExamSetDetailPage;
